import { ICommonInitialState } from "../../shared/types/storeTypes";
import {
  IChangeVacancyApplicationStatus,
  ICreateVacancyApplicationComment,
  IVacancyApplication,
  IVacancyApplicationEvent,
  IVacancyApplicationCreate,
} from "../../api/vacancyApplications/types";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import api from "../../api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";

interface IApplicationsState extends ICommonInitialState {
  vacancyApplications: IVacancyApplication[];
  currentVacancyApplication: IVacancyApplication | null;
}

const initialState: IApplicationsState = {
  vacancyApplications: [],
  currentVacancyApplication: null,
  isLoading: false,
  error: null,
};

export const fetchAllVacancyApplications = createAsyncThunkInstance<
  IVacancyApplication[]
>(
  "vacancyApplications/fetchAllVacancyApplications",
  api.vacancyApplications.getVacancyApplications
);

export const fetchVacancyApplicationById = createAsyncThunkInstance<
  IVacancyApplication,
  string
>(
  "vacancyApplications/fetchVacancyApplicationById",
  api.vacancyApplications.getVacancyApplicationById
);

export const fetchVacancyApplicationCreate = createAsyncThunkInstance<
  IVacancyApplication,
  IVacancyApplicationCreate
>(
  "vacancyApplications/fetchVacancyApplicationById",
  api.vacancyApplications.createVacancyApplication
);

export const fetchCommentVacancyApplicationCreate = createAsyncThunk<
  IVacancyApplicationEvent,
  { params: ICreateVacancyApplicationComment; id: string },
  { rejectValue: AxiosError }
>(
  "vacancyApplications/createComment",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } =
        await api.vacancyApplications.createCommentVacancyApplication(
          id,
          params
        );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchStatusVacancyApplicationChange = createAsyncThunk<
  IVacancyApplicationEvent,
  { params: IChangeVacancyApplicationStatus; id: string },
  { rejectValue: AxiosError }
>(
  "vacancyApplications/changeStatus",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } =
        await api.vacancyApplications.changeStatusVacancyApplication(
          id,
          params
        );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchEventVacancyApplicationUpdate = createAsyncThunk<
  IVacancyApplicationEvent,
  { params: IChangeVacancyApplicationStatus; id: string },
  { rejectValue: AxiosError }
>(
  "vacancyApplications/changeStatus",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } =
        await api.vacancyApplications.updateEventVacancyApplication(
          id,
          params
        );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const deleteVacancyApplicationEvent = createAsyncThunkInstance<
  undefined,
  string
>(
  "vacancyApplications/fetchVacancyApplicationById",
  api.vacancyApplications.deleteEventVacancyApplication
);


export const fetchAnnexFiles = createAsyncThunk<
  undefined,
  { params: { attachments: File }; id: string },
  { rejectValue: AxiosError }
>(
  "vacancyApplications/fetchAnnexFiles",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } = await api.vacancyApplications.addAnnexFiles(id, params);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchDeleteAnnexFiles = createAsyncThunk<
  undefined,
  { params: { attachments_delete: string[] }; id: string },
  { rejectValue: AxiosError }
>(
  "vacancyApplications/fetchDeleteAnnexFiles",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } = await api.vacancyApplications.deleteAnnexFiles(
        id,
        params
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

const vacancyApplicationsSlice = createSlice({
  name: "vacancyApplications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllVacancyApplications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllVacancyApplications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vacancyApplications = action.payload;
      })
      .addCase(fetchVacancyApplicationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVacancyApplicationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVacancyApplication = action.payload;
      });
  },
});

export const vacancyApplicationsReducer = vacancyApplicationsSlice.reducer;
